import * as Sentry from "@sentry/react";

// Gives a chunk-load failure an in-page retry, then one reload per build, then a
// reload when the server already serves a newer build, then the recovery screen.

const RETRY_KEY_PREFIX = "chunk-retry:";
const STALE_KEY_PREFIX = "chunk-stale:";
const RELOAD_DELAY = 300;
// A build gets one reload inside this window; a failure after it gets another.
const RELOAD_COOLDOWN = 10 * 60 * 1000;
const DEFAULT_RETRY_DELAYS = [500, 1500];

let reloadScheduled = false;

// Vite dev server: its HMR client already reloads the page after it re-optimizes a dependency.
export const isDevServer = process.env.NODE_ENV === "development";

const hasWindow = () => typeof window !== "undefined";

const session = {
  get(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      // storage unavailable (private mode / quota) — recovery still reloads once per page life via reloadScheduled
    }
  },
  keys(): string[] {
    try {
      return Object.keys(window.sessionStorage);
    } catch (e) {
      return [];
    }
  },
  remove(key: string) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  }
};

export const isChunkLoadError = (err: any): boolean => {
  if (!err) {
    return false;
  }
  const message = typeof err === "string" ? err : (err && err.message) || "";
  return (
    (err && err.name === "ChunkLoadError") ||
    /Loading (CSS )?chunk \S+ failed/i.test(message) ||
    // Browser messages for a failed dynamic import() or CSS preload of a Vite chunk.
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(message)
  );
};

// Nothing stamps a build id into the bundle; the content hash of the main entry
// script changes on every deploy, which is exactly what the retry marker needs.
export const getBuildId = (): string => {
  if (typeof document === "undefined") {
    return "unknown";
  }
  const script = document.querySelector('script[src*="/static/js/main."]');
  const src = (script && script.getAttribute("src")) || "";
  const match = src.match(/main\.([\w-]+)\./i);
  return (match && match[1]) || src || "unknown";
};

const scheduleReload = (message: string, kind: "retry" | "stale") => {
  reloadScheduled = true;
  // One Sentry issue per reload kind, whatever chunk file name the message holds.
  Sentry.withScope((scope) => {
    scope.setFingerprint(["chunk-load-reload", kind]);
    Sentry.captureMessage(message, Sentry.Severity.Warning);
  });
  setTimeout(() => window.location.reload(), RELOAD_DELAY);
};

// Returns true when a reload has been scheduled (caller should keep its loading
// state visible), false when this build already used its reload and the caller
// must show a manual recovery UI instead.
// True while the build's marker under this prefix is inside the cooldown.
const markerFresh = (prefix: string): boolean => {
  const last = Number(session.get(prefix + getBuildId()) || 0);
  return Boolean(last) && Date.now() - last < RELOAD_COOLDOWN;
};

// Stamps this build's marker and drops markers left by earlier builds, which can never be spent again.
const spendMarker = (prefix: string) => {
  const key = prefix + getBuildId();
  session
    .keys()
    .filter((other) => other.indexOf(prefix) === 0 && other !== key)
    .forEach((other) => session.remove(other));
  session.set(key, String(Date.now()));
};

export const requestRecoveryReload = (reason: string): boolean => {
  if (!hasWindow() || isDevServer) {
    return false;
  }
  if (reloadScheduled) {
    return true;
  }
  if (markerFresh(RETRY_KEY_PREFIX)) {
    return false;
  }
  spendMarker(RETRY_KEY_PREFIX);
  scheduleReload(`Chunk load failure, reloading once: ${reason}`, "retry");
  return true;
};

// Reads the main bundle hash from the HTML the server sends for this page right now.
export const fetchServerBuildId = (): Promise<string> => {
  if (!hasWindow() || typeof fetch !== "function") {
    return Promise.resolve("");
  }
  return fetch(window.location.pathname + window.location.search, {
    cache: "no-store",
    credentials: "same-origin",
    headers: { Accept: "text/html" }
  })
    .then((res) => (res.ok ? res.text() : ""))
    .then((html) => {
      const match = html.match(/\/static\/js\/main\.([\w-]+)\.js/i);
      return (match && match[1]) || "";
    })
    .catch(() => "");
};

// One probe at a time; concurrent callers share its result instead of each fetching the page.
let pendingProbe: Promise<boolean> | null = null;

// Reloads when the server serves a newer build than this page runs; resolves true when a reload is scheduled.
export const reloadIfBuildChanged = (reason: string): Promise<boolean> => {
  if (!hasWindow() || isDevServer) {
    return Promise.resolve(false);
  }
  if (reloadScheduled) {
    return Promise.resolve(true);
  }
  if (pendingProbe) {
    return pendingProbe;
  }
  if (markerFresh(STALE_KEY_PREFIX)) {
    return Promise.resolve(false);
  }
  const current = getBuildId();
  pendingProbe = fetchServerBuildId().then((server) => {
    pendingProbe = null;
    if (!server || server === current || reloadScheduled) {
      return reloadScheduled;
    }
    spendMarker(STALE_KEY_PREFIX);
    scheduleReload(`Chunk load failure on stale build ${current}, server has ${server}: ${reason}`, "stale");
    return true;
  });
  return pendingProbe;
};

// Recovery stages in order: one reload per build, then a reload when the server has a newer build.
// Resolves true when a reload is scheduled, false when the caller must show its recovery UI.
export const recoverFromChunkError = (reason: string): Promise<boolean> =>
  requestRecoveryReload(reason) ? Promise.resolve(true) : reloadIfBuildChanged(reason);

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Calls import() again after each delay; if every attempt fails, runs the recovery stages.
export function retryImport<T>(
  factory: () => Promise<T>,
  delays: number[] = DEFAULT_RETRY_DELAYS
): Promise<T> {
  const attempt = (index: number): Promise<T> =>
    factory().catch((err: any) => {
      if (!isChunkLoadError(err)) {
        throw err;
      }
      if (index < delays.length) {
        return wait(delays[index]).then(() => attempt(index + 1));
      }
      const reason = (err && err.message) || "import failed";
      return recoverFromChunkError(reason).then((willReload) => {
        if (willReload) {
          // Page is about to reload; keep the Suspense fallback on screen meanwhile.
          return new Promise<T>(() => undefined);
        }
        throw err;
      });
    });
  return attempt(0);
}
